import { createRootRoute, createRoute, createRouter, Outlet } from '@tanstack/react-router'

import AppRoot from '@/components/appRoot/AppRoot'
import Header from '@/components/header'
import { currentEnv } from '@/env/vars.ts'
import { isMobile } from '@/hooks/useMobileDetection.ts'
import ChatPage from '@/pages/Chat'
import ImpersonationBanner from '@/pages/components/impersonationBanner'
import Documents from '@/pages/Documents'
import MainPage from '@/pages/Main'
import MobileApp from '@/pages/MobileApp'
import NewsPage from '@/pages/News'
import NotFound from '@/pages/NotFound'
import TasksApprovals from '@/pages/TasksApprovals'
import Translate from '@/pages/Translate'
import { store } from '@/store'
import { partialMessagesAtom } from '@/store/gigiChat/ChatAtom'
// Register your router for maximum type safety
declare module '@tanstack/react-router' {
    interface IRegister {
        router: typeof router
    }
}

// const ua = UA.UAParser(navigator.userAgent)
const rootRoute = createRootRoute({
    component: () => {
        return (
            <>
                <ImpersonationBanner />
                <Header />
                <Outlet />
                <AppRoot />
            </>
        )
    },
    notFoundComponent: NotFound,
})

const homeRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/',
    component: MainPage,
})

const chatRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/chat',
    component: ChatPage,
})

const chatRouteId = createRoute({
    getParentRoute: () => rootRoute,
    path: '/chat/$chatId',
    component: ChatPage,
    onEnter: () => {
        // ensure no partial messages are set in the store
        // this will ensure no flickering
        store.set(partialMessagesAtom, [])
    },
})

const tasksApprovalsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/tasks-approvals',
    component: TasksApprovals,
})

const documentRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/documents',
    component: Documents,
})

const documentRouteWithId = createRoute({
    getParentRoute: () => rootRoute,
    path: '/documents/$chatId',
    component: Documents,
    onEnter: () => {
        // ensure no partial messages are set in the store
        // this will ensure no flickering
        store.set(partialMessagesAtom, [])
    },
})

const translateRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/translate',
    component: Translate,
})

const translateRouteWithId = createRoute({
    getParentRoute: () => rootRoute,
    path: '/translate/$chatId',
    component: Translate,
})

const mobileAppRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/app',
    component: MobileApp,
})

const mobileLockedAppRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/',
    component: MobileApp,
})

const newsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/news',
    component: NewsPage,
})

const publicRoutes = [
    homeRoute,
    chatRoute,
    chatRouteId,
    mobileAppRoute,
    tasksApprovalsRoute,
    documentRoute,
    documentRouteWithId,
    translateRoute,
    translateRouteWithId,
]

//TO DO: find a better way to handle conditional routes in the future
// const routes = isMobile() && currentEnv.features.isEnabled('mobileLock') ? [mobileLockedAppRoute] : [...publicRoutes]

const getRoutes = () => {
    if (isMobile() && currentEnv.features.isEnabled('mobileLock')) {
        return [mobileLockedAppRoute]
    } else if (currentEnv.features.isEnabled('news')) {
        return [...publicRoutes, newsRoute]
    }
    return [...publicRoutes]
}

const routeTree = rootRoute.addChildren(getRoutes())

export const router = createRouter({
    routeTree,
    defaultPreload: 'intent',
})
